// src/steps/VolunteerInfoStep.tsx
import React from "react";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Checkbox from "../components/ui/Checkbox";
import TextArea from "../components/ui/TextArea";
import Button from "../components/ui/Button";
import type { VolunteerInfoState } from "../wizard/wizardState";
import type { WizardPathId } from "../wizard/wizardTypes";
import { getTshirtSizeOptions } from "../constants/tshirtSizes";
import { getChurchOptions } from "../constants/churches";
import { getPickupTypeOptions } from "../constants/pickupTypes";
import { getVolunteerAvailabilityDayOptions } from "../constants/volunteerAvailabilityDays";
import { volunteerAvailabilityDays } from "../constants/volunteerAvailabilityDays";
import { getVolunteerAgeGroupOptions, getVolunteerGradeOptions } from "../constants/volunteerAgeGroups";
import { getPasswordLabel } from "../utils/passwordLabelHelper";
import { formatPhoneNumber } from "../utils/phoneFormatter";
import { useListManagement } from "../hooks/useListManagement";

type Props = {
  volunteers: VolunteerInfoState[];
  onChange: (next: VolunteerInfoState[]) => void;
  errors: Record<string, string>;
  pathId: WizardPathId;
};

function createBlankVolunteer(): VolunteerInfoState {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    ageGroup: "",
    grade: "",
    tshirtSize: "",
    church: "",
    availabilityDays: volunteerAvailabilityDays.map((d) => d.value),
    availabilityNotes: "",
    relationToChild: "",
    pickupType: "",
    passphrase: "",
    comments: "",
  };
}

export default function VolunteerInfoStep({ volunteers, onChange, errors, pathId }: Props) {
  const needsChildRelationAndPickup = pathId !== "volunteerOnly";
  const needsPassword = pathId === "volunteerAndChild";

  const { updateItem, addItem, removeItem } = useListManagement(volunteers, {
    createBlank: createBlankVolunteer,
  });

  const updateVolunteer = (index: number, next: VolunteerInfoState) =>
    onChange(updateItem(index, next));
  const addVolunteer = () => {
    const newVolunteers = addItem();
    // Copy address, last name, and church from first volunteer to new volunteer
    if (volunteers.length > 0) {
      const firstVolunteer = volunteers[0];
      newVolunteers[newVolunteers.length - 1] = {
        ...newVolunteers[newVolunteers.length - 1],
        lastName: firstVolunteer.lastName,
        street: firstVolunteer.street,
        city: firstVolunteer.city,
        state: firstVolunteer.state,
        zip: firstVolunteer.zip,
        church: firstVolunteer.church,
      };
    }
    onChange(newVolunteers);
  };
  const removeVolunteer = (index: number) => onChange(removeItem(index));

  return (
    <div className="space-y-4">
      {errors.volunteers ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {errors.volunteers}
        </div>
      ) : null}

      {volunteers.map((v, idx) => {
        const pfx = `volunteer_${idx}`;

        const toggleAvailabilityDay = (day: string, checked: boolean) => {
          const nextDays = checked
            ? [...v.availabilityDays, day]
            : v.availabilityDays.filter((selected) => selected !== day);
          updateVolunteer(idx, { ...v, availabilityDays: nextDays });
        };

        return (
          <div key={idx} className="rounded-xl border border-slate-200 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold">
                {volunteers.length > 1 ? `Volunteer ${idx + 1}` : "Your Information"}
              </h3>
              <Button
                type="button"
                variant="ghost"
                onClick={() => removeVolunteer(idx)}
                className="px-3 py-2"
              >
                Remove
              </Button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="First Name"
                  value={v.firstName}
                  onChange={(e) => updateVolunteer(idx, { ...v, firstName: e.target.value })}
                  error={errors[`${pfx}_firstName`]}
                />
                <Input
                  label="Last Name"
                  value={v.lastName}
                  onChange={(e) => updateVolunteer(idx, { ...v, lastName: e.target.value })}
                  error={errors[`${pfx}_lastName`]}
                />
              </div>

              <Input
                label="Email"
                value={v.email}
                onChange={(e) => updateVolunteer(idx, { ...v, email: e.target.value })}
                error={errors[`${pfx}_email`]}
                inputMode="email"
                autoComplete="email"
              />

              <Input
                label="Phone"
                value={v.phone}
                onChange={(e) =>
                  updateVolunteer(idx, { ...v, phone: formatPhoneNumber(e.target.value) })
                }
                error={errors[`${pfx}_phone`]}
                inputMode="tel"
                autoComplete="tel"
                placeholder="(xxx) xxx-xxxx"
              />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="Street"
                  value={v.street}
                  onChange={(e) => updateVolunteer(idx, { ...v, street: e.target.value })}
                  error={errors[`${pfx}_street`]}
                />
                <Input
                  label="City"
                  value={v.city}
                  onChange={(e) => updateVolunteer(idx, { ...v, city: e.target.value })}
                  error={errors[`${pfx}_city`]}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Input
                  label="State"
                  value={v.state}
                  onChange={(e) => updateVolunteer(idx, { ...v, state: e.target.value })}
                  error={errors[`${pfx}_state`]}
                />
                <Input
                  label="Zip"
                  value={v.zip}
                  onChange={(e) => updateVolunteer(idx, { ...v, zip: e.target.value })}
                  error={errors[`${pfx}_zip`]}
                  inputMode="numeric"
                />
                <Select
                  label="Age"
                  value={v.ageGroup}
                  onChange={(e) =>
                    updateVolunteer(idx, { ...v, ageGroup: e.target.value, grade: "" })
                  }
                  error={errors[`${pfx}_ageGroup`]}
                  placeholder="Select age"
                  options={getVolunteerAgeGroupOptions()}
                />
              </div>

              {v.ageGroup === "under18" && (
                <Select
                  label="Grade"
                  value={v.grade}
                  onChange={(e) => updateVolunteer(idx, { ...v, grade: e.target.value })}
                  error={errors[`${pfx}_grade`]}
                  placeholder="Select grade"
                  options={getVolunteerGradeOptions()}
                />
              )}

              <Select
                label="Tshirt Size"
                value={v.tshirtSize}
                onChange={(e) => updateVolunteer(idx, { ...v, tshirtSize: e.target.value })}
                error={errors[`${pfx}_tshirtSize`]}
                placeholder="Select size"
                options={getTshirtSizeOptions()}
              />
              <p className="-mt-1 text-xs text-slate-600">
                Last day to guarantee a shirt size is May 29.
              </p>

              <Select
                label="Church"
                value={v.church}
                onChange={(e) => updateVolunteer(idx, { ...v, church: e.target.value })}
                error={errors[`${pfx}_church`]}
                placeholder="Select church"
                options={getChurchOptions()}
              />

              <div className="rounded-lg border border-slate-200 p-3">
                <h3 className="text-sm font-medium text-slate-700">Volunteer Availability</h3>
                <p className="mt-1 text-xs text-slate-600">
                  Select all days that work for your schedule.
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {getVolunteerAvailabilityDayOptions().map((day) => (
                    <Checkbox
                      key={day.value}
                      label={day.label}
                      checked={v.availabilityDays.includes(day.value)}
                      onChange={(e) => toggleAvailabilityDay(day.value, e.target.checked)}
                    />
                  ))}
                </div>
                {errors[`${pfx}_availabilityDays`] ? (
                  <span className="mt-2 block text-xs text-red-600">
                    {errors[`${pfx}_availabilityDays`]}
                  </span>
                ) : null}
              </div>

              <TextArea
                label="Availability Notes (optional)"
                value={v.availabilityNotes}
                onChange={(e) => updateVolunteer(idx, { ...v, availabilityNotes: e.target.value })}
                rows={2}
                placeholder="Share any time constraints for selected days"
              />

              {needsChildRelationAndPickup ? (
                <>
                  <Input
                    label="Relation to Child"
                    value={v.relationToChild}
                    onChange={(e) =>
                      updateVolunteer(idx, { ...v, relationToChild: e.target.value })
                    }
                    error={errors[`${pfx}_relationToChild`]}
                    placeholder="Parent, relative, family friend, etc."
                  />

                  <Select
                    label="Pickup Type"
                    value={v.pickupType}
                    onChange={(e) => updateVolunteer(idx, { ...v, pickupType: e.target.value })}
                    error={errors[`${pfx}_pickupType`]}
                    placeholder="Select pickup type"
                    options={getPickupTypeOptions()}
                  />
                </>
              ) : null}

              {needsPassword ? (
                <Input
                  label={getPasswordLabel(v.pickupType)}
                  value={v.passphrase}
                  onChange={(e) => updateVolunteer(idx, { ...v, passphrase: e.target.value })}
                  error={errors[`${pfx}_passphrase`]}
                />
              ) : null}

              <TextArea
                label="Comments"
                value={v.comments}
                onChange={(e) => updateVolunteer(idx, { ...v, comments: e.target.value })}
                rows={4}
              />
            </div>
          </div>
        );
      })}

      <Button type="button" variant="secondary" onClick={addVolunteer} className="w-full">
        + Add Volunteer
      </Button>
    </div>
  );
}

